import './native-camera';
import './native-feel';
import type { RemoteResources } from './domain/resources';
import { runClientMigrations, importLegacySnapshot, readLegacyBrowserStorage } from './data/migrations';
import { RepositoryStore, createRepositories } from './data/repositories';
import { createLocalDatabase, isNativePlatform } from './platform/create-local-database';
import { ApiClient } from './sync/api-client';
import { SyncEngine } from './sync/sync-engine';

interface LegacyDatabase {
  storage?: Record<string, string>;
}

interface AuthenticationState {
  authenticated?: boolean;
}

function loadScript(source: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = source;
    script.async = false;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Nepodařilo se načíst ${source}`));
    document.head.appendChild(script);
  });
}

async function loadRuntimeConfig(api: ApiClient): Promise<void> {
  if (!api.available) {
    window.__APP_RUNTIME_CONFIG = { aiEnabled: false, storageBackend: 'local' };
    return;
  }
  try {
    window.__APP_RUNTIME_CONFIG = await api.json('/api/runtime-config');
  } catch (error) {
    console.warn('[bootstrap] Runtime config is unavailable:', error);
    window.__APP_RUNTIME_CONFIG = { aiEnabled: false, storageBackend: 'local' };
  }
}

async function start(options: { scripts: string[] }): Promise<void> {
  document.documentElement.dataset.appBoot = 'loading';
  const database = await createLocalDatabase();
  let browserStorage: Storage | null = null;
  try {
    browserStorage = globalThis.localStorage;
  } catch {
    // Some private browsing modes expose localStorage but throw on access.
  }
  let resources = await runClientMigrations(
    database,
    readLegacyBrowserStorage(browserStorage)
  );

  const api = new ApiClient(window.__APP_BUILD_CONFIG__ || {}, isNativePlatform());
  try {
    await api.initializeSession(window.__APP_BUILD_CONFIG__ || {}, isNativePlatform());
  } catch (error) {
    console.warn('[auth] Device pairing deferred:', error);
  }
  let authenticated = false;
  if (api.available) {
    try {
      authenticated = !!(await api.json<AuthenticationState>('/api/auth/me')).authenticated;
    } catch {
      // Sync will retry when the server becomes available.
    }
  }
  if (
    api.available
    && !authenticated
    && resources.every(resource => Object.keys(resource.payload).length === 0)
  ) {
    try {
      const legacy = await api.json<LegacyDatabase>('/api/db');
      if (legacy.storage && Object.keys(legacy.storage).length > 0) {
        resources = await importLegacySnapshot(
          database,
          legacy.storage,
          'migration:legacy-server-snapshot-v1'
        );
      }
    } catch (error) {
      console.info('[bootstrap] No legacy server snapshot imported:', error);
    }
  }

  const store = new RepositoryStore(database, resources);
  const repositories = createRepositories(store);
  const sync = new SyncEngine(api, store);

  window.APP_REPOSITORIES = repositories;
  window.APP_API = {
    available: api.available,
    baseUrl: api.baseUrl,
    fetch: api.fetch.bind(api),
    json: api.json.bind(api)
  };

  const adoptConnectedAccount = async () => {
    const pulled = await api.json<{ contractVersion: number; resources: RemoteResources }>('/api/sync/pull');
    if (pulled.contractVersion !== 1) throw new Error('Unsupported sync contract');
    await store.adoptAccount(pulled.resources);
    await database.setMetadata('account-import-pending', '');
  };
  if (await database.getMetadata('account-import-pending')) {
    // Never upload anonymous defaults if pairing was interrupted before the initial pull.
    await adoptConnectedAccount();
  }
  window.APP_RUNTIME.connectAccount = async code => {
    await sync.pause();
    try {
      await api.pair(code, isNativePlatform());
      await database.setMetadata('account-import-pending', 'true');
      await adoptConnectedAccount();
    } finally {
      if (!await database.getMetadata('account-import-pending')) sync.resume();
    }
  };
  // Pull existing account data before UI initialization can create default records.
  if (authenticated) await sync.sync();
  await loadRuntimeConfig(api);
  for (const script of options.scripts) await loadScript(script);
  window.APP_INITIALIZE?.();

  sync.start();
  document.documentElement.dataset.appBoot = 'ready';
  window.dispatchEvent(new CustomEvent('app:runtime-ready', {
    detail: { storageKind: database.kind }
  }));

  window.APP_RUNTIME.flush = () => store.flush();
  window.APP_RUNTIME.sync = () => sync.sync();
  window.APP_RUNTIME.storageKind = database.kind;
}

window.APP_RUNTIME = {
  start,
  flush: async () => {},
  sync: async () => {},
  connectAccount: async () => { throw new Error('Application is still loading'); },
  storageKind: 'initializing'
};
